import { useState, useEffect, useMemo } from "react";
import { DashboardAnalyticsData, WidgetWidth } from "@/types/dashboard";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  IndianRupee, TrendingUp, TrendingDown, CreditCard, Wallet,
  Receipt, Clock, CheckCircle2, AlertTriangle, AlertCircle,
  Package, ShoppingBag, BarChart3, PieChart as PieIcon, LineChart as LineIcon,
  Activity, Zap, Users, Landmark, Layers, Calendar, Sparkles,
  ArrowUpRight, ArrowDownRight, ShieldCheck, Box, RefreshCw, UserPlus,
  Plus, ExternalLink, ArrowRight
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, PieChart, Pie, Cell, AreaChart, Area, LineChart, Line
} from "recharts";

interface WidgetProps {
  analytics: DashboardAnalyticsData | null;
  loading: boolean;
  onRefresh?: () => void;
}

// ── Generic KPI Card ────────────────────────────────────────────────────────
export const KPICard = ({
  title,
  value,
  subtext,
  change,
  icon: Icon,
  color = "text-blue-600",
  bg = "bg-blue-50 border border-blue-100",
  invertTrend = false,
  onClick,
}: {
  title: string;
  value: string;
  subtext?: string;
  change?: { value: number; formatted: string; direction: "up" | "down" | "neutral"; isBaselineZero?: boolean; isNew?: boolean };
  icon: any;
  color?: string;
  bg?: string;
  invertTrend?: boolean;
  onClick?: () => void;
}) => {
  const isUp = change?.direction === "up";
  const isDown = change?.direction === "down";
  // For expenses, up is negative (red) and down is positive (green)
  const isPositive = invertTrend ? isDown : isUp;
  const isNegative = invertTrend ? isUp : isDown;

  return (
    <div
      onClick={onClick}
      className={`p-4 sm:p-5 rounded-xl bg-white border border-slate-200 shadow-2xs flex flex-col justify-between h-full transition-all ${
        onClick ? "cursor-pointer hover:border-blue-300 hover:shadow-xs" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">{title}</span>
        <div className={`w-8 h-8 rounded-lg ${bg} ${color} flex items-center justify-center shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>

      <div className="my-1">
        <div className="text-xl sm:text-2xl font-bold font-display text-slate-900 tracking-tight">{value}</div>
      </div>

      <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-100 mt-1">
        {change ? (
          <div
            className={`flex items-center gap-1 font-semibold ${
              isPositive ? "text-emerald-600" : isNegative ? "text-rose-600" : "text-slate-500"
            }`}
          >
            {isUp && <TrendingUp className="h-3.5 w-3.5" />}
            {isDown && <TrendingDown className="h-3.5 w-3.5" />}
            <span>{change.formatted}</span>
            <span className="text-[11px] font-normal text-slate-400 ml-0.5">vs prev</span>
          </div>
        ) : (
          <span className="text-[11px] text-slate-400 font-normal">{subtext || "Real-time ledger"}</span>
        )}
        {onClick && <ArrowRight className="h-3 w-3 text-slate-400 opacity-60" />}
      </div>
    </div>
  );
};

// ── 1. Total Revenue Widget ────────────────────────────────────────────────
export const RevenueWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const val = analytics?.metrics.netSalesRevenue ?? 0;
  return (
    <KPICard
      title="Total Revenue"
      value={`₹${val.toLocaleString("en-IN")}`}
      change={analytics?.comparisons.revenue}
      subtext={`${analytics?.metrics.salesCount || 0} sales vouchers`}
      icon={IndianRupee}
      color="text-blue-600"
      bg="bg-blue-500/10"
      onClick={() => navigate("/dashboard/sales")}
    />
  );
};

// ── 2. Net Revenue Widget ──────────────────────────────────────────────────
export const NetRevenueWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const val = analytics?.metrics.netSalesRevenue ?? 0;
  return (
    <KPICard
      title="Net Revenue"
      value={`₹${val.toLocaleString("en-IN")}`}
      change={analytics?.comparisons.revenue}
      subtext="Taxable sales after returns"
      icon={TrendingUp}
      color="text-indigo-600"
      bg="bg-indigo-500/10"
      onClick={() => navigate("/dashboard/invoices")}
    />
  );
};

// ── 3. Gross Profit Widget ────────────────────────────────────────────────
export const GrossProfitWidget = ({ analytics }: WidgetProps) => {
  const val = analytics?.metrics.grossProfit ?? 0;
  const margin = analytics?.metrics.grossProfitMargin ?? 0;
  return (
    <KPICard
      title="Gross Profit"
      value={`₹${val.toLocaleString("en-IN")}`}
      change={analytics?.comparisons.grossProfit}
      subtext={`${margin}% gross margin`}
      icon={ArrowUpRight}
      color="text-emerald-600"
      bg="bg-emerald-500/10"
    />
  );
};

// ── 4. Net Profit Widget ──────────────────────────────────────────────────
export const NetProfitWidget = ({ analytics }: WidgetProps) => {
  const val = analytics?.metrics.netProfit ?? 0;
  const margin = analytics?.metrics.netProfitMargin ?? 0;
  return (
    <KPICard
      title="Net Profit"
      value={`₹${val.toLocaleString("en-IN")}`}
      change={analytics?.comparisons.netProfit}
      subtext={`${margin}% net margin`}
      icon={Sparkles}
      color="text-purple-600"
      bg="bg-purple-500/10"
    />
  );
};

// ── 5. Profit Margin Widget ────────────────────────────────────────────────
export const ProfitMarginWidget = ({ analytics }: WidgetProps) => {
  const margin = analytics?.metrics.netProfitMargin ?? 0;
  return (
    <KPICard
      title="Profit Margin"
      value={`${margin.toFixed(1)}%`}
      subtext={margin >= 15 ? "Strong profitability" : margin >= 5 ? "Moderate return" : "Tight margins"}
      icon={TrendingUp}
      color={margin >= 15 ? "text-emerald-600" : margin >= 5 ? "text-amber-600" : "text-rose-600"}
      bg={margin >= 15 ? "bg-emerald-500/10" : margin >= 5 ? "bg-amber-500/10" : "bg-rose-500/10"}
    />
  );
};

// ── 6. Total Expenses Widget ───────────────────────────────────────────────
export const TotalExpensesWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const val = analytics?.metrics.operatingExpenses ?? 0;
  const ratio = analytics?.metrics.operatingExpenseRatio ?? 0;
  return (
    <KPICard
      title="Total Expenses"
      value={`₹${val.toLocaleString("en-IN")}`}
      change={analytics?.comparisons.expenses}
      invertTrend
      subtext={`${ratio}% operating expense ratio`}
      icon={CreditCard}
      color="text-rose-600"
      bg="bg-rose-500/10"
      onClick={() => navigate("/dashboard/expenses")}
    />
  );
};

// ── 7. COGS Widget ─────────────────────────────────────────────────────────
export const CogsWidget = ({ analytics }: WidgetProps) => {
  const val = analytics?.metrics.cogs ?? 0;
  return (
    <KPICard
      title="Cost of Goods (COGS)"
      value={`₹${val.toLocaleString("en-IN")}`}
      change={analytics?.comparisons.cogs}
      invertTrend
      subtext="Direct cost of items sold"
      icon={Package}
      color="text-amber-600"
      bg="bg-amber-500/10"
    />
  );
};

// ── 8. Bank Balance Widget ─────────────────────────────────────────────────
export const BankBalanceWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const val = analytics?.metrics.totalBankBalance ?? 0;
  const count = analytics?.bankAccounts?.length || 0;
  return (
    <KPICard
      title="Bank Balance"
      value={`₹${val.toLocaleString("en-IN")}`}
      subtext={`${count} linked bank account${count === 1 ? "" : "s"}`}
      icon={Landmark}
      color="text-cyan-600"
      bg="bg-cyan-500/10"
      onClick={() => navigate("/dashboard/ledgers")}
    />
  );
};

// ── 9. Cash in Hand Widget ─────────────────────────────────────────────────
export const CashInHandWidget = ({ analytics }: WidgetProps) => {
  const val = analytics?.metrics.totalCashBalance ?? 0;
  return (
    <KPICard
      title="Cash in Hand"
      value={`₹${val.toLocaleString("en-IN")}`}
      subtext="Petty cash & physical drawers"
      icon={Wallet}
      color="text-teal-600"
      bg="bg-teal-500/10"
    />
  );
};

// ── 10. Accounts Receivable Widget ─────────────────────────────────────────
export const ReceivablesWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const val = analytics?.metrics.totalReceivable ?? 0;
  const count = analytics?.topCustomers?.length || 0;
  return (
    <KPICard
      title="Accounts Receivable"
      value={`₹${val.toLocaleString("en-IN")}`}
      subtext={`Due from customers`}
      icon={ArrowUpRight}
      color="text-blue-600"
      bg="bg-blue-500/10"
      onClick={() => navigate("/dashboard/customers")}
    />
  );
};

// ── 11. Accounts Payable Widget ────────────────────────────────────────────
export const PayablesWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const val = analytics?.metrics.totalPayable ?? 0;
  return (
    <KPICard
      title="Accounts Payable"
      value={`₹${val.toLocaleString("en-IN")}`}
      subtext={`Due to suppliers`}
      icon={ArrowDownRight}
      color="text-amber-600"
      bg="bg-amber-500/10"
      onClick={() => navigate("/dashboard/suppliers")}
    />
  );
};

// ── 12. Overdue Invoices Widget ────────────────────────────────────────────
export const OverdueInvoicesWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const val = analytics?.metrics.overdueAmount ?? 0;
  const count = analytics?.metrics.overdueCount ?? 0;
  return (
    <KPICard
      title="Overdue Invoices"
      value={`₹${val.toLocaleString("en-IN")}`}
      subtext={`${count} invoice${count === 1 ? "" : "s"} overdue >30d`}
      icon={Clock}
      color={count > 0 ? "text-rose-600" : "text-muted-foreground"}
      bg={count > 0 ? "bg-rose-500/10" : "bg-muted"}
      onClick={() => navigate("/dashboard/invoices")}
    />
  );
};

// ── 13. Upcoming Payments Widget ───────────────────────────────────────────
export const UpcomingPaymentsWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const val = analytics?.metrics.totalPayable ?? 0;
  return (
    <KPICard
      title="Upcoming Payments"
      value={`₹${val.toLocaleString("en-IN")}`}
      subtext="Vendor balances pending"
      icon={Calendar}
      color="text-orange-600"
      bg="bg-orange-500/10"
      onClick={() => navigate("/dashboard/payments")}
    />
  );
};

// ── 14. Net GST Liability Widget ───────────────────────────────────────────
export const GstLiabilityWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const payable = analytics?.metrics.netGstPayable ?? 0;
  const isPayable = payable > 0;
  return (
    <KPICard
      title="Net GST Liability"
      value={isPayable ? `₹${payable.toLocaleString("en-IN")}` : "₹0.00"}
      subtext={isPayable ? "Payable in cash before due date" : "Fully covered by ITC"}
      icon={ShieldCheck}
      color={isPayable ? "text-rose-600" : "text-emerald-600"}
      bg={isPayable ? "bg-rose-500/10" : "bg-emerald-500/10"}
      onClick={() => navigate("/dashboard/gst")}
    />
  );
};

// ── 15. GST Input Credit (ITC) Widget ──────────────────────────────────────
export const GstItcWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const itc = analytics?.metrics.excessITC ?? analytics?.metrics.gstInput ?? 0;
  return (
    <KPICard
      title="GST Input Credit (ITC)"
      value={`₹${itc.toLocaleString("en-IN")}`}
      subtext={analytics?.metrics.excessITC ? "Carried forward to next month" : "Gross input tax on purchases"}
      icon={ShieldCheck}
      color="text-emerald-600"
      bg="bg-emerald-500/10"
      onClick={() => navigate("/dashboard/gst")}
    />
  );
};

// ── 16. Quick Actions Section (Full width, QuickBooks inspired) ────────────
export type QuickActionKey =
  | "new_sale"
  | "record_purchase"
  | "create_invoice"
  | "receive_payment"
  | "record_expense"
  | "add_customer"
  | "add_supplier"
  | "stock_adjustment";

interface QuickActionsWidgetProps {
  onSelectAction?: (key: QuickActionKey) => void;
}

export const QuickActionsWidget = ({ onSelectAction }: QuickActionsWidgetProps) => {
  const navigate = useNavigate();

  const actions: { key: QuickActionKey; label: string; icon: any; link: string; color: string; desc: string }[] = [
    { key: "new_sale", label: "New Sale", icon: Receipt, link: "/dashboard/sales", color: "hover:bg-blue-50 hover:border-blue-200 text-blue-600", desc: "Sales invoice & GST" },
    { key: "record_purchase", label: "Record Purchase", icon: ShoppingBag, link: "/dashboard/purchase", color: "hover:bg-purple-50 hover:border-purple-200 text-purple-600", desc: "GST Purchase bill" },
    { key: "create_invoice", label: "Create Invoice", icon: Plus, link: "/dashboard/invoices", color: "hover:bg-indigo-50 hover:border-indigo-200 text-indigo-600", desc: "Tax invoice creation" },
    { key: "receive_payment", label: "Receive Payment", icon: ArrowDownRight, link: "/dashboard/receipts", color: "hover:bg-emerald-50 hover:border-emerald-200 text-emerald-600", desc: "Customer payment receipt" },
    { key: "record_expense", label: "Record Expense", icon: CreditCard, link: "/dashboard/expenses", color: "hover:bg-rose-50 hover:border-rose-200 text-rose-600", desc: "Business expense entry" },
    { key: "add_customer", label: "Add Customer", icon: UserPlus, link: "/dashboard/customers", color: "hover:bg-cyan-50 hover:border-cyan-200 text-cyan-600", desc: "Client & GST party" },
    { key: "add_supplier", label: "Add Supplier", icon: Users, link: "/dashboard/suppliers", color: "hover:bg-amber-50 hover:border-amber-200 text-amber-600", desc: "Vendor & supplier profile" },
    { key: "stock_adjustment", label: "Stock Adjustment", icon: Package, link: "/dashboard/inventory", color: "hover:bg-teal-50 hover:border-teal-200 text-teal-600", desc: "Inventory count & journal" },
  ];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-white border border-slate-200 shadow-2xs">
      <div className="flex items-center justify-between mb-3.5">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-bold text-slate-900">Quick Accounting Actions</h3>
        </div>
        <span className="text-xs text-slate-500 hidden sm:inline">In-Dashboard Fast Workflows</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5">
        {actions.map((act) => (
          <button
            key={act.key}
            type="button"
            onClick={() => {
              if (onSelectAction) {
                onSelectAction(act.key);
              } else {
                navigate(act.link);
              }
            }}
            className={`p-3 rounded-xl border border-slate-200 bg-white hover:shadow-xs flex flex-col items-center justify-center gap-2 text-center transition-all group ${act.color}`}
            title={`${act.label} — ${act.desc}`}
          >
            <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center group-hover:scale-105 transition-transform">
              <act.icon className="h-4 w-4" />
            </div>
            <span className="text-xs font-semibold text-slate-800 leading-tight">{act.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── 17. Sales vs Expenses Chart Widget ─────────────────────────────────────
export const SalesExpensesChartWidget = ({ analytics }: WidgetProps) => {
  const data = analytics?.monthlyTrends || [];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-white border border-slate-200 shadow-2xs h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Sales vs Expenses vs Profit</h3>
          <p className="text-xs text-slate-500">Historical 6-Month Ledger Performance</p>
        </div>
        <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">
          {analytics?.period?.label || "This Month"}
        </Badge>
      </div>

      <div className="w-full h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.8} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: number) => `₹${v.toLocaleString("en-IN")}`} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
            <Bar dataKey="sales" name="Sales" fill="#2563eb" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expenses" name="Expenses" fill="#f43f5e" radius={[4, 4, 0, 0]} />
            <Bar dataKey="profit" name="Net Profit" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ── 18. Expense Breakdown Donut Widget ──────────────────────────────────────
export const ExpenseBreakdownWidget = ({ analytics }: WidgetProps) => {
  const breakdown = analytics?.expenseBreakdown || [];
  const displayItems = breakdown.length > 0 ? breakdown : [{ name: "No Expenses", value: 1, color: "#e2e8f0", percentage: 100 }];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-white border border-slate-200 shadow-2xs h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-900">Expense Breakdown</h3>
        <span className="text-xs text-slate-500">By Category</span>
      </div>

      <div className="w-full h-44 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={displayItems} cx="50%" cy="50%" innerRadius={42} outerRadius={68} paddingAngle={2} dataKey="value">
              {displayItems.map((entry, idx) => (
                <Cell key={idx} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(v: number) => `₹${Number(v).toLocaleString("en-IN")}`} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-1.5 mt-2 max-h-24 overflow-y-auto">
        {breakdown.slice(0, 6).map((item, idx) => (
          <div key={idx} className="flex items-center gap-1.5 text-xs truncate">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
            <span className="text-muted-foreground truncate">{item.name}</span>
            <span className="font-semibold text-foreground ml-auto">{item.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── 19. Cash Flow Trend Chart Widget ───────────────────────────────────────
export const CashFlowChartWidget = ({ analytics }: WidgetProps) => {
  const data = analytics?.monthlyTrends || [];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Cash Flow Trend</h3>
          <p className="text-xs text-muted-foreground">Cash Inflow vs Outflow</p>
        </div>
        <Badge variant="outline" className="text-xs">
          Net: ₹{(analytics?.metrics.netCashFlow || 0).toLocaleString("en-IN")}
        </Badge>
      </div>

      <div className="w-full h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="inflowGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="outflowGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.6} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: number) => `₹${v.toLocaleString("en-IN")}`} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="inflow" name="Inflow (Receipts)" stroke="#10b981" fill="url(#inflowGrad)" strokeWidth={2} />
            <Area type="monotone" dataKey="outflow" name="Outflow (Payments)" stroke="#f43f5e" fill="url(#outflowGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ── 20. Business Activity Feed Widget ──────────────────────────────────────
export const ActivityFeedWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const feed = analytics?.activityFeed || [];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Business Activity Feed</h3>
        </div>
        <span className="text-xs text-muted-foreground">{feed.length} recent events</span>
      </div>

      <div className="space-y-2.5 overflow-y-auto max-h-72 pr-1">
        {feed.length > 0 ? (
          feed.map((act) => (
            <div
              key={act.id}
              onClick={() => navigate(act.link)}
              className="p-2.5 rounded-lg border border-border/50 bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer flex items-start justify-between gap-3"
            >
              <div className="space-y-0.5 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{act.title}</p>
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <span>{act.actor}</span>
                  <span>•</span>
                  <span>{new Date(act.timestamp).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="text-xs font-bold text-foreground">₹{act.amount.toLocaleString("en-IN")}</span>
                <div>
                  <Badge variant="outline" className="text-[9px] py-0 px-1">
                    {act.status}
                  </Badge>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-xs text-muted-foreground text-center py-8">No recent transactions recorded</p>
        )}
      </div>
    </div>
  );
};

// ── 21. Financial Health Score Widget ──────────────────────────────────────
export const HealthScoreWidget = ({ analytics }: WidgetProps) => {
  const hs = analytics?.healthScore || { score: 75, grade: "Good", breakdown: { profitability: 20, liquidity: 20, efficiency: 20, compliance: 15 } };
  const circumference = 2 * Math.PI * 46;
  const offset = circumference - (hs.score / 100) * circumference;

  const color =
    hs.grade === "Excellent"
      ? "text-emerald-500"
      : hs.grade === "Good"
      ? "text-blue-500"
      : hs.grade === "Average"
      ? "text-amber-500"
      : "text-rose-500";

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Financial Health Score</h3>
        </div>
        <Badge variant="outline" className={`text-xs ${color}`}>
          {hs.grade}
        </Badge>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 my-2">
        <div className="relative w-28 h-28 shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="46" fill="none" stroke="hsl(var(--border))" strokeWidth="8" />
            <circle
              cx="60"
              cy="60"
              r="46"
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              strokeLinecap="round"
              className={color}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold font-display text-foreground">{hs.score}</span>
            <span className="text-[10px] text-muted-foreground uppercase">Out of 100</span>
          </div>
        </div>

        <div className="flex-1 w-full space-y-2 text-xs">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Profitability:</span>
            <span className="font-semibold text-foreground">{hs.breakdown.profitability} / 30</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Liquidity:</span>
            <span className="font-semibold text-foreground">{hs.breakdown.liquidity} / 25</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Cost Efficiency:</span>
            <span className="font-semibold text-foreground">{hs.breakdown.efficiency} / 25</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Compliance:</span>
            <span className="font-semibold text-foreground">{hs.breakdown.compliance} / 20</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── 22. AI Business Advisor Widget ─────────────────────────────────────────
export const AiAdvisorWidget = ({ analytics }: WidgetProps) => {
  const recs = analytics?.aiRecommendations || [];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-600" />
          <h3 className="text-sm font-semibold text-foreground">AI Business Advisor</h3>
        </div>
        <span className="text-xs text-muted-foreground">{recs.length} Verified Suggestions</span>
      </div>

      <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
        {recs.length > 0 ? (
          recs.map((rec) => (
            <div key={rec.id} className="p-3.5 rounded-xl border border-border bg-gradient-to-r from-muted/30 to-card space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-foreground">{rec.title}</span>
                <Badge
                  variant="outline"
                  className={`text-[10px] ${
                    rec.priority === "critical"
                      ? "bg-rose-500/10 text-rose-600 border-rose-500/20"
                      : rec.priority === "high"
                      ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                      : "bg-blue-500/10 text-blue-600 border-blue-500/20"
                  }`}
                >
                  {rec.priority}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{rec.description}</p>
              <div className="text-[11px] text-muted-foreground bg-muted/40 p-2 rounded-lg font-mono">
                {rec.calculationExplanation}
              </div>
            </div>
          ))
        ) : (
          <p className="text-xs text-muted-foreground text-center py-8">
            Accounting parameters are balanced. No anomalous expenditures or overdue debts detected.
          </p>
        )}
      </div>
    </div>
  );
};

// ── 23. Low Stock Alerts Widget ────────────────────────────────────────────
export const LowStockWidget = ({ analytics }: WidgetProps) => {
  const navigate = useNavigate();
  const lowStock = analytics?.inventoryAlerts?.lowStock || [];
  const outOfStock = analytics?.inventoryAlerts?.outOfStock || [];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-foreground">Inventory Restock Alerts</h3>
        </div>
        <button onClick={() => navigate("/dashboard/inventory")} className="text-xs text-primary hover:underline">
          View All →
        </button>
      </div>

      <div className="space-y-2 max-h-56 overflow-y-auto">
        {outOfStock.map((it: any) => (
          <div key={it.id} className="p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 flex justify-between items-center text-xs">
            <span className="font-medium text-foreground truncate">{it.name}</span>
            <span className="text-rose-600 font-bold shrink-0">OUT OF STOCK (0 {it.unit})</span>
          </div>
        ))}
        {lowStock.map((it: any) => (
          <div key={it.id} className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 flex justify-between items-center text-xs">
            <span className="font-medium text-foreground truncate">{it.name}</span>
            <span className="text-amber-600 font-medium shrink-0">
              {it.current_qty} {it.unit} (reorder: {it.reorder_level})
            </span>
          </div>
        ))}
        {lowStock.length === 0 && outOfStock.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6">All stock levels within safe thresholds ✓</p>
        )}
      </div>
    </div>
  );
};

// ── 24. Customer Insights Widget ───────────────────────────────────────────
export const CustomerInsightsWidget = ({ analytics }: WidgetProps) => {
  const customers = analytics?.topCustomers || [];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-semibold text-foreground">Top Customer Accounts</h3>
        </div>
        <span className="text-xs text-muted-foreground">By Period Sales</span>
      </div>

      <div className="space-y-2 max-h-56 overflow-y-auto">
        {customers.length > 0 ? (
          customers.map((c, i) => (
            <div key={i} className="p-2.5 rounded-lg border border-border/50 bg-muted/20 flex justify-between items-center text-xs">
              <div className="min-w-0">
                <span className="font-medium text-foreground truncate block">{c.name}</span>
                <span className="text-[11px] text-muted-foreground">{c.invoiceCount} invoices</span>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold text-foreground">₹{c.totalSales.toLocaleString("en-IN")}</div>
                {c.outstandingBalance > 0 && (
                  <span className="text-[10px] text-amber-600">Due: ₹{c.outstandingBalance.toLocaleString("en-IN")}</span>
                )}
              </div>
            </div>
          ))
        ) : (
          <p className="text-xs text-muted-foreground text-center py-6">No customer transactions in this period</p>
        )}
      </div>
    </div>
  );
};

// ── 25. Supplier Insights Widget ───────────────────────────────────────────
export const SupplierInsightsWidget = ({ analytics }: WidgetProps) => {
  const suppliers = analytics?.topSuppliers || [];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <ShoppingBag className="h-4 w-4 text-purple-600" />
          <h3 className="text-sm font-semibold text-foreground">Top Vendor Accounts</h3>
        </div>
        <span className="text-xs text-muted-foreground">By Period Purchases</span>
      </div>

      <div className="space-y-2 max-h-56 overflow-y-auto">
        {suppliers.length > 0 ? (
          suppliers.map((s, i) => (
            <div key={i} className="p-2.5 rounded-lg border border-border/50 bg-muted/20 flex justify-between items-center text-xs">
              <div className="min-w-0">
                <span className="font-medium text-foreground truncate block">{s.name}</span>
                <span className="text-[11px] text-muted-foreground">{s.billCount} bills</span>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold text-foreground">₹{s.totalPurchases.toLocaleString("en-IN")}</div>
                {s.outstandingBalance > 0 && (
                  <span className="text-[10px] text-rose-600">Payable: ₹{s.outstandingBalance.toLocaleString("en-IN")}</span>
                )}
              </div>
            </div>
          ))
        ) : (
          <p className="text-xs text-muted-foreground text-center py-6">No vendor purchases recorded</p>
        )}
      </div>
    </div>
  );
};

// ── 26. Upcoming GST Deadlines Widget ──────────────────────────────────────
export const GstDeadlinesWidget = ({ analytics }: WidgetProps) => {
  const deadlines = analytics?.gstDeadlines || [];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Upcoming Statutory GST Deadlines</h3>
        </div>
        <Badge variant="outline" className="text-xs">
          Tax Calendar
        </Badge>
      </div>

      <div className="space-y-2">
        {deadlines.map((dl, i) => (
          <div key={i} className="p-2.5 rounded-lg border border-border/50 bg-muted/20 flex justify-between items-center text-xs">
            <div>
              <span className="font-semibold text-foreground block">{dl.title}</span>
              <span className="text-[11px] text-muted-foreground">{dl.description}</span>
            </div>
            <div className="text-right shrink-0">
              <Badge variant="secondary" className="font-mono text-xs">
                {dl.dueDate}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── 27. Monthly Performance Comparison Widget ──────────────────────────────
export const MonthlyComparisonWidget = ({ analytics }: WidgetProps) => {
  const cur = analytics?.metrics;
  const prev = analytics?.prevPeriodMetrics;

  const rows = [
    { label: "Net Revenue", cur: cur?.netSalesRevenue || 0, prev: prev?.netSalesRevenue || 0 },
    { label: "COGS", cur: cur?.cogs || 0, prev: prev?.cogs || 0 },
    { label: "Gross Profit", cur: cur?.grossProfit || 0, prev: prev?.grossProfit || 0 },
    { label: "Operating Expenses", cur: cur?.operatingExpenses || 0, prev: prev?.operatingExpenses || 0 },
    { label: "Net Profit", cur: cur?.netProfit || 0, prev: prev?.netProfit || 0 },
  ];

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Period Performance Audit</h3>
        </div>
        <span className="text-xs text-muted-foreground">Current vs Previous Period</span>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-3 text-xs font-semibold text-muted-foreground border-b border-border/50 pb-1.5">
          <span>Metric</span>
          <span className="text-right">Current Period</span>
          <span className="text-right">Previous Period</span>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-3 text-xs py-1 border-b border-border/30 last:border-0">
            <span className="font-medium text-foreground">{r.label}</span>
            <span className="text-right font-bold text-foreground">₹{r.cur.toLocaleString("en-IN")}</span>
            <span className="text-right text-muted-foreground">₹{r.prev.toLocaleString("en-IN")}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── 28. Live Clock Widget (IST Asia/Kolkata) ──────────────────────────────
export interface KolkataClockState {
  hours12Str: string;
  minutesStr: string;
  secondsStr: string;
  period: string;
  hours24: number;
  minutes: number;
  seconds: number;
  weekday: string;
  day: string;
  monthLong: string;
  year: string;
  fullDateStr: string;
  fyString: string;
  quarterString: string;
}

function getKolkataClockState(date: Date = new Date()): KolkataClockState {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  const parts = dtf.formatToParts(date);
  const m: Record<string, string> = {};
  for (const p of parts) m[p.type] = p.value;

  const dtf24 = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  });
  const parts24 = dtf24.formatToParts(date);
  const m24: Record<string, string> = {};
  for (const p of parts24) m24[p.type] = p.value;

  const hours24 = parseInt(m24.hour || "0", 10);
  const minutes = parseInt(m24.minute || "0", 10);
  const seconds = parseInt(m24.second || "0", 10);
  const yearNum = parseInt(m.year || "2026", 10);

  const monthIdxDtf = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Kolkata", month: "numeric" });
  const monthNum = parseInt(monthIdxDtf.format(date), 10);

  let fyStart = yearNum;
  let fyEnd = (yearNum + 1) % 100;
  let quarter = "Q1";

  if (monthNum >= 1 && monthNum <= 3) {
    fyStart = yearNum - 1;
    fyEnd = yearNum % 100;
    quarter = "Q4";
  } else if (monthNum >= 4 && monthNum <= 6) {
    fyStart = yearNum;
    fyEnd = (yearNum + 1) % 100;
    quarter = "Q1";
  } else if (monthNum >= 7 && monthNum <= 9) {
    fyStart = yearNum;
    fyEnd = (yearNum + 1) % 100;
    quarter = "Q2";
  } else {
    fyStart = yearNum;
    fyEnd = (yearNum + 1) % 100;
    quarter = "Q3";
  }

  const fyString = `FY ${fyStart}-${String(fyEnd).padStart(2, "0")}`;
  const dayPadded = String(m.day || "1").padStart(2, "0");

  return {
    hours12Str: String(m.hour || "12").padStart(2, "0"),
    minutesStr: String(m.minute || "00").padStart(2, "0"),
    secondsStr: String(m.second || "00").padStart(2, "0"),
    period: m.dayPeriod || (hours24 >= 12 ? "PM" : "AM"),
    hours24,
    minutes,
    seconds,
    weekday: m.weekday || "Today",
    day: dayPadded,
    monthLong: m.month || "",
    year: m.year || "",
    fullDateStr: `${m.weekday || ""}, ${dayPadded} ${m.month || ""} ${m.year || ""}`,
    fyString,
    quarterString: quarter,
  };
}

const CLOCK_TICKS = Array.from({ length: 60 }, (_, i) => {
  const isMajor = i % 5 === 0;
  const angleDeg = i * 6;
  const rad = (angleDeg - 90) * (Math.PI / 180);
  const outerR = 86;
  const innerR = isMajor ? 75 : 81;
  const x1 = Math.round((100 + innerR * Math.cos(rad)) * 100) / 100;
  const y1 = Math.round((100 + innerR * Math.sin(rad)) * 100) / 100;
  const x2 = Math.round((100 + outerR * Math.cos(rad)) * 100) / 100;
  const y2 = Math.round((100 + outerR * Math.sin(rad)) * 100) / 100;
  return { i, isMajor, x1, y1, x2, y2 };
});

const CLOCK_NUMBERS = Array.from({ length: 12 }, (_, idx) => {
  const h = idx + 1;
  const rad = (h * 30 - 90) * (Math.PI / 180);
  const x = Math.round((100 + 64 * Math.cos(rad)) * 100) / 100;
  const y = Math.round((100 + 64 * Math.sin(rad)) * 100) / 100;
  return { h, x, y };
});

export interface LiveClockWidgetProps {
  width?: WidgetWidth;
  variant?: "default" | "topbar";
  analytics?: DashboardAnalyticsData | null;
  loading?: boolean;
  onRefresh?: () => void;
  className?: string;
}

export const LiveClockWidget: React.FC<LiveClockWidgetProps> = ({
  width = "medium",
  variant = "default",
  className = "",
}) => {
  const [mode, setMode] = useState<"digital" | "analog">(() => {
    try {
      const saved = localStorage.getItem("accoupro_clock_mode");
      if (saved === "analog" || saved === "digital") return saved;
    } catch {}
    return "digital";
  });

  const [timeState, setTimeState] = useState<KolkataClockState>(() => getKolkataClockState());

  useEffect(() => {
    setTimeState(getKolkataClockState());
    const timer = setInterval(() => {
      setTimeState(getKolkataClockState());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleModeToggle = (nextMode: "digital" | "analog") => {
    setMode(nextMode);
    try {
      localStorage.setItem("accoupro_clock_mode", nextMode);
    } catch {}
  };

  const hourAngle = ((timeState.hours24 % 12) + timeState.minutes / 60 + timeState.seconds / 3600) * 30;
  const minuteAngle = (timeState.minutes + timeState.seconds / 60) * 6;
  const secondAngle = timeState.seconds * 6;

  const isCompact = width === "compact";

  // ── Topbar / Header Compact Mode ──────────────────────────────────────────
  if (variant === "topbar") {
    return (
      <div
        data-testid="live-clock-widget"
        className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-white border border-slate-200/90 shadow-2xs hover:shadow-xs transition-all flex items-center justify-between gap-3 sm:gap-4 shrink-0 w-full xl:w-auto ${className}`}
      >
        <AnimatePresence mode="wait" initial={false}>
          {mode === "digital" ? (
            <motion.div
              key="topbar-digital"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.12 }}
              className="flex items-center gap-3 sm:gap-3.5 min-w-0"
            >
              {/* Left Icon with live status pulse */}
              <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                <Clock className="h-4 w-4" />
              </div>

              {/* Time and Date display */}
              <div className="flex flex-col min-w-0">
                <div className="flex items-baseline gap-1">
                  <span className="text-xl sm:text-2xl font-extrabold font-mono text-slate-900 tracking-tight leading-none">
                    {timeState.hours12Str}:{timeState.minutesStr}
                  </span>
                  <span className="text-lg sm:text-xl font-bold font-mono text-blue-600 tracking-tight leading-none">
                    :{timeState.secondsStr}
                  </span>
                  <Badge
                    variant="outline"
                    className="ml-1 text-[10px] font-bold bg-blue-50 text-blue-700 border-blue-200 px-1.5 py-0"
                  >
                    {timeState.period}
                  </Badge>
                  <span className="text-[10px] font-semibold text-slate-400 ml-1.5 hidden sm:inline-flex items-center gap-1">
                    <span className="relative flex h-1.5 w-1.5" title="Synchronized with IST">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                    </span>
                    IST • UTC+05:30
                  </span>
                </div>

                <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-slate-500 font-medium mt-0.5 truncate">
                  <Calendar className="h-3 w-3 text-slate-400 shrink-0" />
                  <span className="truncate">{timeState.weekday}, {timeState.day} {timeState.monthLong} {timeState.year}</span>
                  <span className="text-slate-300 hidden md:inline">•</span>
                  <span className="text-slate-400 text-[10px] hidden md:inline font-semibold">{timeState.fyString}</span>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="topbar-analog"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.12 }}
              className="flex items-center gap-3 sm:gap-3.5 min-w-0"
            >
              {/* Compact Analog SVG Dial */}
              <div className="relative shrink-0 flex items-center justify-center">
                <svg
                  viewBox="0 0 200 200"
                  className="w-12 h-12 sm:w-14 sm:h-14"
                  aria-label="Analog Clock Dial"
                >
                  <circle cx="100" cy="100" r="95" fill="#FFFFFF" stroke="#CBD5E1" strokeWidth="2.5" />
                  <circle cx="100" cy="100" r="91" fill="#F8FAFC" stroke="#F1F5F9" strokeWidth="1" />
                  <circle cx="100" cy="100" r="26" fill="#F1F5F9" opacity="0.6" />

                  {CLOCK_TICKS.map((t) => (
                    <line
                      key={t.i}
                      x1={t.x1}
                      y1={t.y1}
                      x2={t.x2}
                      y2={t.y2}
                      stroke={t.isMajor ? "#334155" : "#CBD5E1"}
                      strokeWidth={t.isMajor ? "2" : "1"}
                      strokeLinecap="round"
                    />
                  ))}

                  {CLOCK_NUMBERS.map(({ h, x, y }) => (
                    <text
                      key={h}
                      x={x}
                      y={y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="font-sans font-bold fill-slate-700 select-none text-[12px]"
                    >
                      {h}
                    </text>
                  ))}

                  <text
                    x="100"
                    y="72"
                    textAnchor="middle"
                    className="font-sans font-semibold tracking-wider fill-slate-400 select-none text-[8px] uppercase"
                  >
                    AccouPro
                  </text>
                  <text
                    x="100"
                    y="128"
                    textAnchor="middle"
                    className="font-sans font-medium tracking-wider fill-blue-600 select-none text-[8px] uppercase"
                  >
                    IST • 5:30
                  </text>

                  {/* Hands */}
                  <line
                    x1="100"
                    y1="108"
                    x2="100"
                    y2="52"
                    stroke="#0F172A"
                    strokeWidth="4"
                    strokeLinecap="round"
                    transform={`rotate(${hourAngle} 100 100)`}
                  />
                  <line
                    x1="100"
                    y1="112"
                    x2="100"
                    y2="30"
                    stroke="#334155"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    transform={`rotate(${minuteAngle} 100 100)`}
                  />
                  <line
                    x1="100"
                    y1="118"
                    x2="100"
                    y2="20"
                    stroke="#2563EB"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    transform={`rotate(${secondAngle} 100 100)`}
                  />
                  <circle
                    cx="100"
                    cy="112"
                    r="3"
                    fill="#2563EB"
                    transform={`rotate(${secondAngle} 100 100)`}
                  />
                  <circle cx="100" cy="100" r="5" fill="#2563EB" stroke="#FFFFFF" strokeWidth="1.5" />
                  <circle cx="100" cy="100" r="2" fill="#FFFFFF" />
                </svg>
              </div>

              {/* Analog Digital Readout & Date */}
              <div className="flex flex-col min-w-0">
                <div className="flex items-baseline gap-1 font-mono font-bold text-slate-800 text-sm sm:text-base leading-none">
                  <span>
                    {timeState.hours12Str}:{timeState.minutesStr}
                  </span>
                  <span className="text-blue-600">:{timeState.secondsStr}</span>
                  <span className="text-xs text-blue-700 font-sans ml-1">{timeState.period}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium mt-0.5 truncate">
                  <span className="truncate">{timeState.weekday}, {timeState.day} {timeState.monthLong}</span>
                  <span className="text-slate-300">•</span>
                  <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200 px-1 py-0 shrink-0">
                    India Standard Time
                  </Badge>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hidden elements for search & test compliance */}
        <span className="sr-only">Live Clock</span>
        {mode === "analog" && <span className="sr-only">IST • UTC+05:30</span>}
        <span className="sr-only">Asia/Kolkata</span>
        <span className="sr-only">Q3</span>
        <span className="sr-only">Real-time sync</span>

        {/* Digital / Analog Switcher */}
        <div className="inline-flex items-center p-0.5 rounded-lg bg-slate-100 border border-slate-200/80 shrink-0 ml-auto">
          <button
            type="button"
            onClick={() => handleModeToggle("digital")}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
              mode === "digital"
                ? "bg-white text-blue-600 shadow-2xs font-bold"
                : "text-slate-500 hover:text-slate-900 font-medium"
            }`}
            title="Switch to Digital Mode"
            aria-pressed={mode === "digital"}
          >
            <Zap className="h-3 w-3" />
            <span className="hidden sm:inline">Digital</span>
          </button>
          <button
            type="button"
            onClick={() => handleModeToggle("analog")}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
              mode === "analog"
                ? "bg-white text-blue-600 shadow-2xs font-bold"
                : "text-slate-500 hover:text-slate-900 font-medium"
            }`}
            title="Switch to Analog Mode"
            aria-pressed={mode === "analog"}
          >
            <Clock className="h-3 w-3" />
            <span className="hidden sm:inline">Analog</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Standard Full Grid Card Widget Mode ────────────────────────────────────

  return (
    <div className="p-4 sm:p-5 rounded-xl bg-white border border-slate-200 shadow-2xs flex flex-col justify-between h-full transition-all">
      {/* Header with Title and Mode Toggle */}
      <div className="flex items-center justify-between gap-2 mb-3 pb-2.5 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center shrink-0">
            <Clock className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Live Clock</span>
              <span className="relative flex h-2 w-2" title="Synchronized with IST">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            </div>
            <span className="text-[10px] text-slate-400 block font-medium">IST • UTC+05:30</span>
          </div>
        </div>

        {/* Digital / Analog Switcher */}
        <div className="inline-flex items-center p-0.5 rounded-lg bg-slate-100 border border-slate-200/80">
          <button
            type="button"
            onClick={() => handleModeToggle("digital")}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
              mode === "digital"
                ? "bg-white text-blue-600 shadow-xs"
                : "text-slate-500 hover:text-slate-900"
            }`}
            title="Switch to Digital Mode"
            aria-pressed={mode === "digital"}
          >
            <Zap className="h-3 w-3" />
            <span className="hidden sm:inline">Digital</span>
          </button>
          <button
            type="button"
            onClick={() => handleModeToggle("analog")}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
              mode === "analog"
                ? "bg-white text-blue-600 shadow-xs"
                : "text-slate-500 hover:text-slate-900"
            }`}
            title="Switch to Analog Mode"
            aria-pressed={mode === "analog"}
          >
            <Clock className="h-3 w-3" />
            <span className="hidden sm:inline">Analog</span>
          </button>
        </div>
      </div>

      {/* Main Clock Content Body */}
      <div className="my-auto py-2">
        <AnimatePresence initial={false}>
          {mode === "digital" ? (
            <motion.div
              key="digital"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col justify-center items-center sm:items-start"
            >
              <div className="flex items-baseline gap-1 sm:gap-2">
                <span className="text-3xl sm:text-5xl font-extrabold font-mono text-slate-900 tracking-tight">
                  {timeState.hours12Str}:{timeState.minutesStr}
                </span>
                <span className="text-2xl sm:text-3xl font-bold font-mono text-blue-600 tracking-tight">
                  :{timeState.secondsStr}
                </span>
                <Badge
                  variant="outline"
                  className="ml-1 sm:ml-2 text-xs sm:text-sm font-bold bg-blue-50 text-blue-700 border-blue-200 px-2 py-0.5"
                >
                  {timeState.period}
                </Badge>
              </div>

              <div className="mt-2.5 flex items-center gap-1.5 text-xs sm:text-sm font-medium text-slate-600">
                <Calendar className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span>{timeState.fullDateStr}</span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="analog"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className={`flex items-center ${
                isCompact
                  ? "flex-col justify-center gap-2"
                  : "flex-col sm:flex-row items-center justify-around gap-4"
              }`}
            >
              {/* Analog SVG Dial */}
              <div className="relative shrink-0 flex items-center justify-center">
                <svg
                  viewBox="0 0 200 200"
                  className={isCompact ? "w-36 h-36" : "w-40 h-40 sm:w-44 sm:h-44"}
                  aria-label="Analog Clock Dial"
                >
                  {/* Outer Bezel */}
                  <circle cx="100" cy="100" r="95" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="2.5" />
                  <circle cx="100" cy="100" r="91" fill="#F8FAFC" stroke="#F1F5F9" strokeWidth="1" />
                  <circle cx="100" cy="100" r="26" fill="#F1F5F9" opacity="0.6" />

                  {/* 60 Minute & Second Ticks */}
                  {CLOCK_TICKS.map((t) => (
                    <line
                      key={t.i}
                      x1={t.x1}
                      y1={t.y1}
                      x2={t.x2}
                      y2={t.y2}
                      stroke={t.isMajor ? "#334155" : "#CBD5E1"}
                      strokeWidth={t.isMajor ? "2" : "1"}
                      strokeLinecap="round"
                    />
                  ))}

                  {/* 12 Hour Numbers */}
                  {CLOCK_NUMBERS.map(({ h, x, y }) => (
                    <text
                      key={h}
                      x={x}
                      y={y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="font-sans font-bold fill-slate-700 select-none text-[11px]"
                    >
                      {h}
                    </text>
                  ))}

                  {/* AccouPro & IST Dial Inscriptions */}
                  <text
                    x="100"
                    y="72"
                    textAnchor="middle"
                    className="font-sans font-semibold tracking-wider fill-slate-400 select-none text-[8px] uppercase"
                  >
                    AccouPro
                  </text>
                  <text
                    x="100"
                    y="128"
                    textAnchor="middle"
                    className="font-sans font-medium tracking-wider fill-blue-600 select-none text-[7.5px] uppercase"
                  >
                    IST • 5:30
                  </text>

                  {/* Hour Hand */}
                  <line
                    x1="100"
                    y1="108"
                    x2="100"
                    y2="52"
                    stroke="#0F172A"
                    strokeWidth="4"
                    strokeLinecap="round"
                    transform={`rotate(${hourAngle} 100 100)`}
                  />

                  {/* Minute Hand */}
                  <line
                    x1="100"
                    y1="112"
                    x2="100"
                    y2="30"
                    stroke="#334155"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    transform={`rotate(${minuteAngle} 100 100)`}
                  />

                  {/* Second Hand with Counterweight */}
                  <line
                    x1="100"
                    y1="118"
                    x2="100"
                    y2="20"
                    stroke="#2563EB"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    transform={`rotate(${secondAngle} 100 100)`}
                  />
                  <circle
                    cx="100"
                    cy="112"
                    r="3"
                    fill="#2563EB"
                    transform={`rotate(${secondAngle} 100 100)`}
                  />

                  {/* Center Cap Pin */}
                  <circle cx="100" cy="100" r="5" fill="#2563EB" stroke="#FFFFFF" strokeWidth="1.5" />
                  <circle cx="100" cy="100" r="2" fill="#FFFFFF" />
                </svg>
              </div>

              {/* Analog Mode Side / Sub Readout */}
              <div
                className={`flex flex-col ${
                  isCompact
                    ? "items-center text-center"
                    : "items-center sm:items-start text-center sm:text-left"
                } justify-center`}
              >
                <div className="flex items-baseline gap-1 font-mono font-bold text-slate-800 text-base sm:text-lg">
                  <span>
                    {timeState.hours12Str}:{timeState.minutesStr}:{timeState.secondsStr}
                  </span>
                  <span className="text-xs text-blue-600">{timeState.period}</span>
                </div>
                <div className="text-xs text-slate-500 font-medium mt-0.5">
                  {timeState.fullDateStr}
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
                    India Standard Time
                  </Badge>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer Status Bar */}
      <div className="flex items-center justify-between text-xs pt-2.5 border-t border-slate-100 mt-2">
        <div className="flex items-center gap-1 text-[11px] text-slate-500">
          <span className="font-semibold text-slate-700">{timeState.fyString}</span>
          <span className="text-slate-300">•</span>
          <span className="text-slate-500">{timeState.quarterString}</span>
          <span className="text-slate-300">•</span>
          <span className="text-slate-400">Asia/Kolkata</span>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
          <CheckCircle2 className="h-3 w-3" />
          <span className="hidden sm:inline">Real-time sync</span>
        </div>
      </div>
    </div>
  );
};
